import { faCarrot, faPotFood } from '@fortawesome/pro-solid-svg-icons';
import { Accordion, Badge, ListGroup } from 'react-bootstrap';
import { tss } from 'tss-react';

import { MenuPlannerItemsBulkResponse } from 'api/menuPlanner/GetMenuPlannerItemsBulk';
import ProductHeader from 'components/desktop/ShoppingCart/Detail/misc/ProductHeader';
import SourceHeader from 'components/desktop/ShoppingCart/Detail/misc/SourceHeader';
import Tabs from 'components/desktop/_general/Tabs/Tabs';
import { Functor } from 'functions/Functor';
import useCultureCode from 'hooks/redux/useCultureCode';
import useTranslation from 'hooks/useTranslation';
import { Appearance } from 'types/ShoppingCart/Appearance';
import { ProductList } from 'types/ShoppingCart/ProductList';
import { ShoppingCart } from 'types/ShoppingCart/ShoppingCart';

interface IProps {
	item: Appearance.Type;
	itemKey: number;
	cart: ShoppingCart.Type;
	itemOrders: MenuPlannerItemsBulkResponse | null;
}

const SourceItem = ({ item, itemKey, cart, itemOrders }: IProps) => {
	const { classes } = useStyles();
	const { tl, t } = useTranslation();
	const cultureCode = useCultureCode();

	const allItemsSortedByDate = itemOrders
		? itemOrders.items
				.slice() // Copy to avoid mutating original array
				.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
		: [];

	const ProductListTab = () => {
		return (
			<div style={{ marginTop: '1rem' }}>
				<p className={classes.count}>
					{
						Functor.From(ShoppingCart.getProductsFromAppearanceId(cart, item.id))
							.map(ProductList.group)
							.get().length
					}{' '}
					{t('shoppingCart:PRODUCTS')}
				</p>
				<ListGroup as="ol">
					{Functor.From(ShoppingCart.getProductsFromAppearanceId(cart, item.id))
						.map(ProductList.group)
						.get()
						.map((product, productKey) => {
							return (
								<ListGroup.Item
									as="li"
									style={{ display: 'flex' }}
									key={productKey}
								>
									<ProductHeader product={product} key={productKey} />
								</ListGroup.Item>
							);
						})}
				</ListGroup>
			</div>
		);
	};

	const OrdersTab = () => {
		return (
			<div style={{ marginTop: '1rem' }}>
				<ListGroup as="ol">
					<div style={{ overflowX: 'auto', maxWidth: '100%' }}>
						<table className="table table-striped table-bordered">
							<thead className="thead-dark">
								<tr>
									<th
										style={{
											zIndex: 2,
										}}
										className={classes.thStyle}
									>
										{t('shoppingCart:COST_CENTER')}: {itemOrders?.costCenter}
									</th>
									<th
										key={item.id}
										style={{
											minWidth: '5rem',
											textAlign: 'center',
										}}
									></th>
								</tr>
							</thead>
							<tbody>
								{allItemsSortedByDate.map((menuPlan, rowIdx) => (
									<tr key={rowIdx}>
										<td
											className={classes.thStyle}
											style={{
												zIndex: 1,
												display: 'flex',
												justifyContent: 'space-between',
											}}
										>
											{tl(menuPlan.titleTranslations)}{' '}
											<Badge bg="secondary">
												{new Date(menuPlan.date).toLocaleDateString(
													cultureCode,
													{
														weekday: 'long',
														day: '2-digit',
														month: '2-digit',
													}
												)}
											</Badge>
										</td>
										<td
											key={menuPlan.id}
											style={{
												textAlign: 'center',
											}}
										>
											{menuPlan.quantity ?? 0}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</ListGroup>
			</div>
		);
	};

	return (
		<Accordion.Item eventKey={itemKey.toString()} key={itemKey}>
			<Accordion.Header>
				<SourceHeader
					source={item}
					cart={cart}
					itemOrders={itemOrders ? itemOrders : null}
				></SourceHeader>
			</Accordion.Header>
			<Accordion.Body style={{ backgroundColor: '#FAFAFA' }}>
				{itemOrders ? (
					<div style={{ backgroundColor: 'white' }}>
						<Tabs
							localStorageKey={'ordersTab'}
							tabs={[
								{
									name: t('shoppingCart:MEALS'),
									content: <OrdersTab></OrdersTab>,
									icon: faPotFood,
								},
								{
									name: t('shoppingCart:PRODUCTS'),
									content: <ProductListTab></ProductListTab>,
									icon: faCarrot,
								},
							]}
						/>
					</div>
				) : (
					<ProductListTab></ProductListTab>
				)}
			</Accordion.Body>
		</Accordion.Item>
	);
};

export default SourceItem;

export const useStyles = tss.create({
	container: {
		display: 'flex',
		flexDirection: 'column',
		gap: '20px',
		marginTop: '20px',
	},
	item: {
		padding: '10px',
		border: '1px solid grey',
	},
	count: { fontWeight: 'lighter', fontStyle: 'italic' },
	thStyle: {
		position: 'sticky',
		left: 0,
		minWidth: '16rem',
	},
});
